import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { SpecialistIcon } from '../components/chatHome/specialistIcons';
import { BackLink } from '../components/ui/BackLink';
import AgentGalleryTeaser from '../components/agent/AgentGalleryTeaser';
import { useAuth } from '../contexts/AuthContext';
import { getPublicAgents, type PublicAgent } from '../services/agentService';
import { toSlug } from '../utils/slug';

/** sessionStorage key: the agent a visitor asked to chat with, consumed by ChatWorkspace after (login →) dashboard. */
export const START_AGENT_KEY = 'wt_start_agent';

/**
 * PUBLIC agent profile — /agent/{slug} (+ /agent/{slug}/details). Crawlable (no auth), so it's
 * an SEO/GEO surface: each catalog persona gets a static-looking page with its expertise,
 * frameworks, and skills + per-page <meta> and Person/JSON-LD structured data.
 */
const PublicAgentPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agents, setAgents] = useState<PublicAgent[] | null>(null);

  // STATIC snapshot first (public/agents.json — served by hosting, fast, no CORS), API as backup.
  // This makes the crawlable /agent/{slug} page render at BUILD-TIME PRERENDER (where a call to the
  // live apiGateway from the localhost origin is blocked by CORS and would bake "Agent not found"),
  // and stays fast + cache-friendly in production. The snapshot is kept in sync with the catalog by a
  // pre-commit test; the live API is only used if the static file is missing/empty.
  useEffect(() => {
    let alive = true;
    fetch('/agents.json')
      .then((r) => r.json())
      .then((d) => (d.agents?.length ? d.agents : getPublicAgents()))
      .catch(() => getPublicAgents())
      .then((list) => { if (alive) setAgents(list); })
      .catch(() => { if (alive) setAgents([]); });
    // avatar_image lives in per-env DB overrides (not the code-derived static snapshot), so overlay
    // the live values from the API once loaded — the page still renders instantly from the snapshot.
    getPublicAgents()
      .then((live) => {
        const byId = new Map(live.map((a) => [a.id, a.avatar_image]));
        if (alive) setAgents((prev) => prev?.map((a) => (byId.get(a.id) ? { ...a, avatar_image: byId.get(a.id) } : a)) || prev);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const agent = agents?.find((a) => toSlug(a.name) === slug || a.id === slug) || null;

  // Stash which agent to chat with, then send the visitor to the app. ProtectedRoute bounces a signed-out
  // visitor to /login; either way they land on the dashboard, where ChatWorkspace opens the 1-1 chat.
  const startChat = () => {
    if (agent) {
      try { sessionStorage.setItem(START_AGENT_KEY, JSON.stringify({ id: agent.id, name: agent.name, persona: agent.persona, avatar_icon: agent.avatar_icon })); } catch { /* ignore */ }
    }
    navigate('/dashboard');
  };

  // Per-route SEO: title/description + Person JSON-LD. Cleaned up on unmount.
  useEffect(() => {
    if (!agent) return;
    const prevTitle = document.title;
    document.title = `${agent.name} — ${agent.persona} | Wisetree AI`;
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = `${agent.name}, a ${agent.persona} on Wisetree AI. ${agent.description}`;
    document.head.appendChild(meta);
    // Point the canonical at the PRIMARY domain's URL — so a crawled non-prod copy
    // (staging.wisetree.ai, *.web.app) defers to wisetree.ai instead of competing.
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = canonical?.href;
    if (canonical) canonical.href = `https://wisetree.ai/agent/${toSlug(agent.name)}`;
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    const img = typeof agent.avatar_image === 'string' && /^https?:\/\//.test(agent.avatar_image) ? agent.avatar_image : undefined;
    ld.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'Person',
      name: agent.name, jobTitle: agent.persona, description: agent.description,
      // knowsAbout = the agent's expertise (domain + frameworks + skills) — a strong "who is an expert in X"
      // signal for answer/generative engines (GEO/AEO).
      knowsAbout: [agent.domain, ...(agent.frameworks || []), ...(agent.skills || [])].filter(Boolean),
      ...(img ? { image: img } : {}),
      worksFor: { '@type': 'Organization', name: 'Wisetree AI', url: 'https://wisetree.ai/' },
      url: `https://wisetree.ai/agent/${toSlug(agent.name)}`,
    });
    document.head.appendChild(ld);
    return () => {
      document.title = prevTitle; meta.remove(); ld.remove();
      if (canonical && prevCanonical) canonical.href = prevCanonical;
    };
  }, [agent]);

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between">
          <BackLink to="/" label="Wisetree AI" />
          <Link to="/agents" className="text-sm text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 underline">All agents</Link>
        </div>

        {agents === null ? (
          <p className="mt-8 text-secondary-400">Loading…</p>
        ) : !agent ? (
          <div className="mt-8">
            <h1 className="text-2xl font-bold">Agent not found</h1>
            <p className="mt-2 text-secondary-500">No such specialist. <Link to="/agents" className="underline">Browse all agents</Link>.</p>
          </div>
        ) : (
          <article className="mt-8">
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-2xl bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center overflow-hidden">
                {agent.avatar_image
                  ? <img src={agent.avatar_image} alt={agent.name} className="w-full h-full object-cover" />
                  : <SpecialistIcon name={agent.avatar_icon} className="w-7 h-7 text-secondary-600 dark:text-secondary-300" />}
              </span>
              <div>
                <h1 className="text-2xl font-bold leading-tight">{agent.name}</h1>
                <p className="text-secondary-500">{agent.persona}</p>
              </div>
            </div>

            <p className="mt-5 text-secondary-700 dark:text-secondary-200">{agent.description}</p>
            {agent.personality && <p className="mt-3 text-sm text-secondary-500 italic">{agent.personality}</p>}

            {agent.backstory && (
              <section className="mt-6 border-l-2 border-secondary-200 dark:border-secondary-700 pl-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-400">Backstory</h2>
                <p className="mt-2 text-sm leading-relaxed text-secondary-600 dark:text-secondary-300">{agent.backstory}</p>
              </section>
            )}

            {agent.frameworks?.length > 0 && (
              <section className="mt-6">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-400">Thinks with</h2>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {agent.frameworks.map((f) => (
                    <li key={f} className="text-xs rounded-full border border-secondary-200 dark:border-secondary-700 px-2.5 py-1 text-secondary-700 dark:text-secondary-200">{f}</li>
                  ))}
                </ul>
              </section>
            )}

            {agent.skills?.length > 0 && (
              <section className="mt-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-400">Skills</h2>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {agent.skills.map((s) => (
                    <li key={s} className="text-xs rounded-full bg-secondary-100 dark:bg-secondary-800 px-2.5 py-1 text-secondary-600 dark:text-secondary-300">{s}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Social gallery sits under Skills and above the chat CTA. */}
            <AgentGalleryTeaser agentId={agent.id} agentName={agent.name} authed={!!user} onOpen={startChat} />

            <div className="mt-8 rounded-2xl border border-secondary-200 dark:border-secondary-700 p-5">
              <p className="font-semibold">Chat with {agent.name} on Wisetree AI</p>
              <p className="mt-1 text-sm text-secondary-500">Start a private 1-1 chat with {agent.name} — add more specialists to the group anytime.</p>
              <button onClick={startChat} className="mt-3 inline-block rounded-xl bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100">Start a conversation</button>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default PublicAgentPage;
